import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Eye, Archive, ArchiveRestore, Trash2, CheckCircle2, Search, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import KrakenLoader from "@/components/KrakenLoader";

const TYPE_LABEL = {
  contact: "Contact",
  tryout: "Tryout",
  camp: "Camp Registration",
  donation: "Donation",
};

const STATUS_COLOR = {
  new: "bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/40",
  reviewed: "bg-green-500/15 text-green-300 border-green-400/40",
  archived: "bg-slate-500/15 text-slate-400 border-slate-400/30",
};

const FIELD_LABELS = {
  player_name: "Player Name", parent_name: "Parent Name", age: "Age", dob: "Date of Birth",
  positions: "Positions", email: "Email", phone: "Phone", medical_notes: "Medical Notes",
  waiver_accepted: "Waiver Accepted", name: "Name", topic: "Topic", message: "Message",
  donor_name: "Donor Name", amount: "Amount", purpose: "Purpose", notes: "Notes",
  created_at: "Submitted", id: "ID", status: "Status", is_archived: "Archived", is_deleted: "Deleted",
};

const HIDDEN_KEYS = new Set(["_type", "_name", "_email", "is_deleted", "deleted_at"]);

const formatKey = (k) => FIELD_LABELS[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const formatVal = (v) => {
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (v == null || v === "") return "—";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    try { return new Date(v).toLocaleString(); } catch { return v; }
  }
  return String(v);
};

export default function SubmissionsManager() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("active"); // active | archived | deleted
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [detail, setDetail] = useState(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (view === "active") params.set("archived", "false");
    else if (view === "archived") params.set("archived", "true");
    else if (view === "deleted") params.set("include_deleted", "true");
    if (typeFilter !== "all") params.set("type", typeFilter);
    if (statusFilter !== "all") params.set("status", statusFilter);
    api.get(`/admin/submissions?${params.toString()}`).then((r) => {
      let data = r.data || [];
      if (view === "deleted") data = data.filter((d) => d.is_deleted);
      setRows(data);
      setSelected(new Set());
    }).finally(() => setLoading(false));
  };
  useEffect(load, [view, typeFilter, statusFilter]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.toLowerCase();
    return rows.filter((r) =>
      Object.entries(r).some(([k, v]) => k !== "id" && typeof v !== "object" && String(v ?? "").toLowerCase().includes(needle))
    );
  }, [rows, q]);

  const setStatus = async (r, status) => {
    try { await api.post(`/admin/submissions/${r._type}/${r.id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const archive = (r) => setStatus(r, "archived");
  const markReviewed = (r) => setStatus(r, "reviewed");
  const restore = async (r) => {
    try { await api.post(`/admin/submissions/${r._type}/${r.id}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const softDelete = async (r) => {
    if (!window.confirm(`Delete this ${TYPE_LABEL[r._type]} submission from ${r._name}? It will be moved to Deleted and can be restored.`)) return;
    try { await api.delete(`/admin/submissions/${r._type}/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const allSelected = filtered.length > 0 && filtered.every((r) => selected.has(`${r._type}:${r.id}`));
  const toggleAll = () => {
    const next = new Set();
    if (!allSelected) filtered.forEach((r) => next.add(`${r._type}:${r.id}`));
    setSelected(next);
  };
  const toggleOne = (r) => {
    const key = `${r._type}:${r.id}`;
    const next = new Set(selected);
    next.has(key) ? next.delete(key) : next.add(key);
    setSelected(next);
  };

  const bulkAction = async (action) => {
    if (selected.size === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${selected.size} submission(s)? They will be moved to Deleted.`)) return;
    const items = Array.from(selected).map((k) => { const [type, id] = k.split(":"); return { type, id }; });
    try {
      const { data } = await api.post("/admin/submissions/bulk", { items, action });
      toast.success(`${data.updated} updated`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="submissions-manager">
      {/* Filters bar */}
      <div className="kr-card rounded-xl p-4 mb-4 flex flex-col gap-3">
        <div className="flex flex-wrap gap-2 items-center">
          {[
            ["active", "Active"],
            ["archived", "Archived"],
            ["deleted", "Deleted"],
          ].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`subs-view-${k}`}
              className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-widest ${view === k ? "bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/40" : "text-slate-300 border border-white/10 hover:text-white"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" data-testid="subs-type-filter">
            <option value="all">All Types</option>
            {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" data-testid="subs-status-filter">
            <option value="all">All Statuses</option>
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="archived">Archived</option>
          </select>
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input placeholder="Search name, email, message…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-[#050A15] border border-white/10 rounded-md text-sm" data-testid="subs-search" />
          </div>
        </div>
        {isAdmin && selected.size > 0 && (
          <div className="flex flex-wrap gap-2 items-center pt-2 border-t border-white/10">
            <span className="text-sm text-slate-300">{selected.size} selected</span>
            <button onClick={() => bulkAction("mark_reviewed")} className="btn-ghost text-xs"><CheckCircle2 size={12} /> Mark Reviewed</button>
            <button onClick={() => bulkAction("archive")} className="btn-ghost text-xs"><Archive size={12} /> Archive</button>
            <button onClick={() => bulkAction("delete")} className="btn-ghost text-xs text-red-400"><Trash2 size={12} /> Delete</button>
          </div>
        )}
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 lg:hidden">
        {filtered.map((r) => (
          <div key={`${r._type}:${r.id}`} className="kr-card rounded-xl p-4" data-testid={`subs-row-${r.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest text-[#00E5FF]">{TYPE_LABEL[r._type]}</div>
                <div className="font-semibold truncate">{r._name || "—"}</div>
                <div className="text-xs text-slate-400 truncate">{r._email}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-1 ${STATUS_COLOR[r.status] || ""}`}>{r.status}</span>
            </div>
            <div className="text-xs text-slate-500 mb-3">{formatVal(r.created_at)}</div>
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => setDetail(r)} className="btn-ghost text-xs"><Eye size={12} /> View</button>
              {!r.is_archived && r.status !== "reviewed" && <button onClick={() => markReviewed(r)} className="btn-ghost text-xs"><CheckCircle2 size={12} /> Reviewed</button>}
              {!r.is_archived && !r.is_deleted && <button onClick={() => archive(r)} className="btn-ghost text-xs"><Archive size={12} /> Archive</button>}
              {(r.is_archived || r.is_deleted) && isAdmin && <button onClick={() => restore(r)} className="btn-ghost text-xs"><ArchiveRestore size={12} /> Restore</button>}
              {isAdmin && !r.is_deleted && <button onClick={() => softDelete(r)} className="btn-ghost text-xs text-red-400"><Trash2 size={12} /> Delete</button>}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden lg:block kr-card rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="subs-table">
          <thead className="bg-[#14213D] uppercase text-[10px] tracking-widest text-slate-300">
            <tr>
              <th className="p-3 w-10"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="subs-select-all" /></th>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Submitted</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={`${r._type}:${r.id}`} className="border-t border-white/10 hover:bg-white/5" data-testid={`subs-row-${r.id}`}>
                <td className="p-3"><input type="checkbox" checked={selected.has(`${r._type}:${r.id}`)} onChange={() => toggleOne(r)} /></td>
                <td className="p-3 text-[#00E5FF] text-xs uppercase tracking-widest">{TYPE_LABEL[r._type]}</td>
                <td className="p-3 font-semibold">{r._name || "—"}</td>
                <td className="p-3 text-slate-300 break-all">{r._email}</td>
                <td className="p-3"><span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-1 ${STATUS_COLOR[r.status] || ""}`}>{r.status}</span></td>
                <td className="p-3 text-slate-400 text-xs whitespace-nowrap">{formatVal(r.created_at)}</td>
                <td className="p-3">
                  <div className="flex gap-1 flex-wrap">
                    <button onClick={() => setDetail(r)} title="View" className="p-1.5 hover:bg-white/10 rounded text-[#00E5FF]"><Eye size={14} /></button>
                    {!r.is_archived && r.status !== "reviewed" && <button onClick={() => markReviewed(r)} title="Mark Reviewed" className="p-1.5 hover:bg-white/10 rounded text-green-400"><CheckCircle2 size={14} /></button>}
                    {!r.is_archived && !r.is_deleted && <button onClick={() => archive(r)} title="Archive" className="p-1.5 hover:bg-white/10 rounded"><Archive size={14} /></button>}
                    {(r.is_archived || r.is_deleted) && isAdmin && <button onClick={() => restore(r)} title="Restore" className="p-1.5 hover:bg-white/10 rounded text-yellow-400"><ArchiveRestore size={14} /></button>}
                    {isAdmin && !r.is_deleted && <button onClick={() => softDelete(r)} title="Delete" className="p-1.5 hover:bg-white/10 rounded text-red-400"><Trash2 size={14} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && filtered.length === 0 && <div className="p-12 text-center text-slate-500">No submissions match your filters.</div>}
        {loading && <KrakenLoader label="Loading submissions…" testId="subs-loader" />}
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={() => setDetail(null)} data-testid="subs-detail-modal">
          <div onClick={(e) => e.stopPropagation()} className="kr-card rounded-2xl p-6 sm:p-8 max-w-xl w-full my-8 relative">
            <button onClick={() => setDetail(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X size={20} /></button>
            <div className="eyebrow mb-1">{TYPE_LABEL[detail._type]}</div>
            <h2 className="font-display text-2xl uppercase mb-6">Submission Details</h2>
            <dl className="space-y-2">
              {Object.entries(detail)
                .filter(([k]) => !HIDDEN_KEYS.has(k))
                .map(([k, v]) => (
                  <div key={k} className="grid sm:grid-cols-[160px_1fr] gap-1 sm:gap-4 py-2 border-b border-white/10 last:border-0">
                    <dt className="text-xs uppercase tracking-widest text-[#00E5FF]">{formatKey(k)}</dt>
                    <dd className="text-sm text-slate-200 break-words whitespace-pre-wrap">{formatVal(v)}</dd>
                  </div>
                ))}
            </dl>
            <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-white/10">
              {detail._email && <a href={`mailto:${detail._email}`} className="btn-primary text-sm">Reply by Email</a>}
              {detail.phone && <a href={`tel:${detail.phone}`} className="btn-ghost text-sm">Call {detail.phone}</a>}
              {detail.status !== "reviewed" && !detail.is_archived && <button onClick={() => { markReviewed(detail); setDetail(null); }} className="btn-ghost text-sm"><CheckCircle2 size={14} /> Mark Reviewed</button>}
              {!detail.is_archived && !detail.is_deleted && <button onClick={() => { archive(detail); setDetail(null); }} className="btn-ghost text-sm"><Archive size={14} /> Archive</button>}
              {(detail.is_archived || detail.is_deleted) && isAdmin && <button onClick={() => { restore(detail); setDetail(null); }} className="btn-ghost text-sm"><ArchiveRestore size={14} /> Restore</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
